import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shareToWhatsApp } from "@/lib/share";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, ShieldCheck, User, KeyRound, Copy, Check, Power, ShoppingCart, PackageCheck, Sparkles } from "lucide-react";

const ROLE_LABELS = { admin: "Yönetici", user: "Kullanıcı", purchasing: "Satınalmacı", warehouse: "Depo Kontrol" };
const EXTRA_ROLE_LABELS = { purchasing: "Satın Alma", warehouse: "Depo Kontrol" };
const EXTRA_OPTIONS = [{ value: "purchasing", label: "Satın Alma" }, { value: "warehouse", label: "Depo Kontrol" }];
const ROLE_ICONS = { admin: ShieldCheck, user: User, purchasing: ShoppingCart, warehouse: PackageCheck };
const ROLE_COLORS = {
  admin: "bg-emerald-100 text-emerald-700",
  user: "bg-slate-100 text-slate-600",
  purchasing: "bg-amber-100 text-amber-700",
  warehouse: "bg-violet-100 text-violet-700",
};

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user", extra_roles: [], auto_generate: true });
  // Password reset requests
  const [resets, setResets] = useState([]);
  // Show generated password
  const [generated, setGenerated] = useState(null); // { user: {...}, new_password: "..." }
  const [copied, setCopied] = useState(false);

  const loadAll = async () => {
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/password-resets")]);
      setItems(u.data);
      setResets(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { loadAll(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({ name: "", email: "", password: "", role: "user", extra_roles: [], auto_generate: true });
    setOpen(true);
  };
  const startEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, extra_roles: u.extra_roles || [], auto_generate: false });
    setOpen(true);
  };
  const toggleExtra = (v) => setForm((f) => {
    const has = (f.extra_roles || []).includes(v);
    return { ...f, extra_roles: has ? f.extra_roles.filter((x) => x !== v) : [...(f.extra_roles || []), v] };
  });

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, extra_roles: form.extra_roles || [] };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Güncellendi");
        setOpen(false);
        loadAll();
      } else {
        const payload = {
          name: form.name,
          email: form.email,
          role: form.role,
          extra_roles: form.extra_roles || [],
          auto_generate: form.auto_generate,
        };
        if (!form.auto_generate && form.password) payload.password = form.password;
        const { data } = await api.post("/users", payload);
        setOpen(false);
        loadAll();
        if (data.generated_password) {
          setGenerated({ user: data, new_password: data.generated_password });
        } else {
          toast.success("Kullanıcı eklendi");
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/users/${id}`); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try {
      const { data } = await api.patch(`/users/${u.id}/active`, { is_active: !(u.is_active ?? true) });
      toast.success(data.is_active ? `${u.name} aktif edildi` : `${u.name} pasif yapıldı`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const regeneratePwd = async (u) => {
    if (!window.confirm(`${u.name} için yeni şifre üretilsin mi? Mevcut şifre geçersiz olur.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/regenerate-password`);
      setGenerated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resolveReset = async (r) => {
    try {
      const { data } = await api.post(`/password-resets/${r.id}/resolve`);
      setGenerated(data);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const dismissReset = async (r) => {
    if (!window.confirm("Bu talebi reddetmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/password-resets/${r.id}`); loadAll(); toast.success("Reddedildi"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyPassword = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated.new_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const shareWA = () => {
    if (!generated) return;
    const text = `Merhaba ${generated.user.name},\n\nYeni şifreniz: ${generated.new_password}\n\nİlk girişten sonra şifrenizi değiştirmenizi öneririz.`;
    shareToWhatsApp({ text, title: "Yeni Şifre", waUrl: `https://wa.me/?text=${encodeURIComponent(text)}` });
  };

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Kullanıcılar</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} kullanıcı kayıtlı</p>
        </div>
        <Button data-testid="add-user-btn" className="h-10 gap-2 bg-slate-900 hover:bg-slate-800" onClick={startCreate}>
          <Plus className="h-4 w-4" /> Kullanıcı Ekle
        </Button>
      </div>

      {/* Pending password reset requests */}
      {resets.length > 0 && (
        <div data-testid="reset-requests-panel" className="mb-5 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound className="h-4 w-4 text-amber-700" />
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-amber-900">
              Şifre Sıfırlama Talepleri ({resets.length})
            </h2>
          </div>
          <div className="space-y-2">
            {resets.map((r) => (
              <div key={r.id} data-testid={`reset-row-${r.id}`} className="bg-white border border-amber-100 rounded-md p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900">{r.user_name}</div>
                  <div className="text-xs text-slate-500">{r.user_email} · {new Date(r.created_at).toLocaleString("tr-TR")}</div>
                  {r.message && <div className="text-xs text-slate-600 mt-1 italic">"{r.message}"</div>}
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`reset-resolve-${r.id}`}
                    size="sm"
                    className="h-9 bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                    onClick={() => resolveReset(r)}
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Yeni Şifre Üret
                  </Button>
                  <Button
                    data-testid={`reset-dismiss-${r.id}`}
                    size="sm"
                    variant="outline"
                    className="h-9"
                    onClick={() => dismissReset(r)}
                  >
                    Reddet
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User list */}
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {items.map((u) => {
          const RoleIcon = ROLE_ICONS[u.role] || User;
          const active = u.is_active !== false;
          return (
            <div key={u.id} data-testid={`user-row-${u.email}`} className={`p-4 flex items-center justify-between gap-3 flex-wrap ${!active ? "bg-slate-50/60" : ""}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`h-9 w-9 rounded-full flex items-center justify-center ${ROLE_COLORS[u.role] || ROLE_COLORS.user}`}>
                  <RoleIcon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className={`font-medium ${active ? "text-slate-900" : "text-slate-500 line-through"}`}>{u.name}</div>
                    {!active && <span className="text-[10px] font-bold uppercase bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">Pasif</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{u.email} · <span className="font-medium">{ROLE_LABELS[u.role] || u.role}</span></div>
                  {(u.extra_roles || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(u.extra_roles || []).map((r) => (
                        <span key={r} data-testid={`user-extra-${u.email}-${r}`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                          +{EXTRA_ROLE_LABELS[r] || r}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-1 flex-wrap">
                <button
                  data-testid={`user-toggle-active-${u.email}`}
                  className={`p-2 rounded-md hover:bg-slate-100 ${active ? "text-emerald-600" : "text-rose-500"}`}
                  onClick={() => toggleActive(u)}
                  title={active ? "Pasif yap" : "Aktif yap"}
                >
                  <Power className="h-4 w-4" />
                </button>
                <button
                  data-testid={`user-regen-${u.email}`}
                  className="p-2 rounded-md hover:bg-amber-50 text-amber-600"
                  onClick={() => regeneratePwd(u)}
                  title="Şifre üret"
                >
                  <KeyRound className="h-4 w-4" />
                </button>
                <button data-testid={`edit-user-${u.email}`} className="p-2 rounded-md hover:bg-slate-100 text-slate-500" onClick={() => startEdit(u)}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button data-testid={`delete-user-${u.email}`} className="p-2 rounded-md hover:bg-rose-50 text-rose-500" onClick={() => del(u.id)}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/edit user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Ad Soyad</Label>
              <Input data-testid="user-form-name" className="mt-1.5 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">E-posta</Label>
              <Input data-testid="user-form-email" type="email" disabled={!!editing} className="mt-1.5 h-11" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user" data-testid="role-opt-user">Kullanıcı</SelectItem>
                  <SelectItem value="purchasing" data-testid="role-opt-purchasing">Satınalmacı</SelectItem>
                  <SelectItem value="warehouse" data-testid="role-opt-warehouse">Depo Kontrol</SelectItem>
                  <SelectItem value="admin" data-testid="role-opt-admin">Yönetici</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Ek Yetkiler <span className="font-normal text-slate-400">(opsiyonel)</span></label>
              <p className="text-xs text-slate-400 mt-0.5">Ana rolün yanında ek görev. Ör: "Satın Alma" seçili kullanıcı, satınalmacıya iletilen taleplerde satın alma yapabilir.</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {EXTRA_OPTIONS.filter((o) => o.value !== form.role).map((o) => {
                  const checked = (form.extra_roles || []).includes(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      data-testid={`extra-role-${o.value}`}
                      onClick={() => toggleExtra(o.value)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${checked ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                    >
                      {checked ? "✓ " : ""}{o.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {!editing && (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="user-form-auto-pw"
                    checked={form.auto_generate}
                    onChange={(e) => setForm({ ...form, auto_generate: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-slate-900"
                  />
                  <span className="text-sm font-medium text-slate-700 inline-flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-amber-600" />
                    Otomatik şifre üret (önerilen)
                  </span>
                </label>
                {!form.auto_generate && (
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Şifre</Label>
                    <Input data-testid="user-form-password" type="text" className="mt-1.5 h-10" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  </div>
                )}
              </div>
            )}
            {editing && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Yeni Şifre (boş = değiştirme)</Label>
                <Input data-testid="user-form-password" type="text" className="mt-1.5 h-11" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button data-testid="user-form-save" className="bg-slate-900 hover:bg-slate-800" onClick={save}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generated password reveal dialog */}
      <Dialog open={!!generated} onOpenChange={(o) => { if (!o) setGenerated(null); }}>
        <DialogContent data-testid="generated-pw-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Sparkles className="h-5 w-5" /> Yeni Şifre Oluşturuldu
            </DialogTitle>
            <DialogDescription>
              Bu şifreyi <strong>şimdi kopyalayın</strong> ve kullanıcıyla paylaşın. Bu pencere kapandıktan sonra bir daha gösterilmez.
            </DialogDescription>
          </DialogHeader>
          {generated && (
            <div className="space-y-3">
              <div>
                <div className="text-xs text-slate-500">Kullanıcı</div>
                <div className="font-medium text-slate-900">{generated.user.name}</div>
                <div className="text-xs text-slate-500">{generated.user.email}</div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Yeni Şifre</Label>
                <div className="mt-1.5 relative">
                  <Input
                    data-testid="generated-pw-input"
                    readOnly
                    value={generated.new_password}
                    className="h-12 pr-12 font-mono text-base font-semibold tracking-wider bg-emerald-50 border-emerald-200 text-emerald-900"
                  />
                  <button
                    data-testid="copy-pw-btn"
                    type="button"
                    onClick={copyPassword}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-emerald-100 text-emerald-700"
                    aria-label="Kopyala"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="share-wa-pw" onClick={shareWA} className="flex-1 h-10 bg-[#25D366] hover:bg-[#128C7E] text-white gap-2">
                  WhatsApp ile Paylaş
                </Button>
                <Button data-testid="copy-pw-text-btn" variant="outline" onClick={copyPassword} className="flex-1 h-10 gap-2">
                  {copied ? <><Check className="h-4 w-4" /> Kopyalandı</> : <><Copy className="h-4 w-4" /> Kopyala</>}
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="generated-pw-close" onClick={() => setGenerated(null)} className="w-full bg-slate-900 hover:bg-slate-800">
              Tamamdır
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
